/**
 * useTouchDragDeploy - Hook for mobile touch drag-and-drop deployment
 * 
 * Allows users to touch and drag orders from their hand, releasing on the
 * left half (front zone) or right half (flank zone) of the screen to deploy.
 * 
 * Features:
 * - Touch drag from hand cards
 * - Visual feedback with dragged card preview
 * - Zone highlighting during drag
 * - Left half = front zone, Right half = flank zone
 * - Dead zone in center (10% of screen width) cancels deployment
 * - Only activates above the carousel area
 * - Works alongside existing click-click deployment
 */

import { useState, useCallback, useRef } from 'react';

const useTouchDragDeploy = ({
  isMobile,
  yourTurn,
}) => {
  // State for tracking touch drag
  const [isDragging, setIsDragging] = useState(false);
  const [draggedOrder, setDraggedOrder] = useState(null);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [targetZone, setTargetZone] = useState(null); // 'front', 'flank', or null
  
  // Refs for tracking touch state
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const hasDraggedRef = useRef(false);
  const orderRef = useRef(null);
  
  // Threshold for considering it a drag vs tap (in pixels)
  const DRAG_THRESHOLD = 15;
  // Dead zone in center of screen (percentage of screen width)
  const CENTER_DEAD_ZONE_PERCENT = 0.10;
  // Minimum upward drag distance to consider deployment (pixels)
  const MIN_UPWARD_DRAG = 50;
  
  /**
   * Determine which zone the current position targets
   * @param {number} x - Current X position
   * @param {number} screenWidth - Screen width
   * @returns {'front' | 'flank' | null}
   */
  const getTargetZoneFromPosition = useCallback((x, screenWidth) => {
    const centerX = screenWidth / 2;
    const deadZoneHalfWidth = (screenWidth * CENTER_DEAD_ZONE_PERCENT) / 2;
    
    // Check if in dead zone (center)
    if (x >= centerX - deadZoneHalfWidth && x <= centerX + deadZoneHalfWidth) {
      return null; // Dead zone - no target
    }
    
    // Left half = front zone
    if (x < centerX) {
      return 'front';
    }
    
    // Right half = flank zone
    return 'flank';
  }, []);
  
  /**
   * Handle touch start on a card
   */
  const handleTouchStart = useCallback((e, order) => {
    console.log('📱 TOUCH START:', { isMobile, yourTurn, orderName: order?.name });
    if (!isMobile || !yourTurn) return;
    
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
    hasDraggedRef.current = false;
    orderRef.current = order;
    console.log('📱 Touch registered at:', { x: touch.clientX, y: touch.clientY });
    
    // Don't prevent default yet - allow normal scrolling behavior initially
  }, [isMobile, yourTurn]);
  
  /**
   * Handle touch move
   * Note: Cannot call preventDefault() on passive listeners in React.
   * The drag is still tracked even without preventing scroll.
   */
  const handleTouchMove = useCallback((e) => {
    if (!isMobile || !yourTurn || !orderRef.current) return;
    
    const touch = e.touches[0];
    if (!touch) return;
    
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // Check if we've moved enough to consider this a drag
    if (!hasDraggedRef.current && distance > DRAG_THRESHOLD) {
      // Only start drag if moving upward (negative Y)
      if (deltaY < -DRAG_THRESHOLD / 2) {
        hasDraggedRef.current = true;
        setIsDragging(true);
        setDraggedOrder(orderRef.current);
        console.log('📱 DRAG STARTED for:', orderRef.current?.name);
        // Note: Scrolling is prevented via CSS touchAction: 'none' on carousel cards
      }
    }
    
    if (hasDraggedRef.current) {
      // Note: Cannot preventDefault on passive listeners in React
      // Scrolling is prevented via CSS touchAction: 'none' on carousel cards
      
      // Update drag position
      setDragPosition({
        x: touch.clientX,
        y: touch.clientY
      });
      
      // Determine target zone based on X position
      const screenWidth = window.innerWidth;
      const upwardDistance = touchStartRef.current.y - touch.clientY;
      
      // Only show zone targeting if dragged upward enough
      if (upwardDistance > MIN_UPWARD_DRAG) {
        const zone = getTargetZoneFromPosition(touch.clientX, screenWidth);
        setTargetZone(zone);
        console.log('📱 DRAG TARGETING:', { zone, upwardDistance, x: touch.clientX });
      } else {
        setTargetZone(null);
      }
    }
  }, [isMobile, yourTurn, getTargetZoneFromPosition]);
  
  /**
   * Handle touch end - returns deployment info if drag was successful
   * @returns {{ wasDragging: boolean, order: object|null, zone: string|null }}
   */
  const handleTouchEnd = useCallback((e) => {
    if (!isMobile || !yourTurn) {
      return { wasDragging: false, order: null, zone: null };
    }
    
    const order = orderRef.current;
    const wasDragging = hasDraggedRef.current && isDragging;
    const zone = targetZone;
    
    // Reset refs immediately
    hasDraggedRef.current = false;
    orderRef.current = null;
    
    // Prevent the default touch end behavior if we were dragging
    if (wasDragging) {
      e.preventDefault();
    }
    
    // Reset all drag state
    setIsDragging(false);
    setDraggedOrder(null);
    setDragPosition({ x: 0, y: 0 });
    setTargetZone(null);
    
    // Return info for the caller to handle deployment
    return { wasDragging, order, zone };
  }, [isMobile, yourTurn, isDragging, targetZone]);
  
  /**
   * Cancel drag (for external cancellation)
   */
  const cancelDrag = useCallback(() => {
    hasDraggedRef.current = false;
    orderRef.current = null;
    setIsDragging(false);
    setDraggedOrder(null);
    setDragPosition({ x: 0, y: 0 });
    setTargetZone(null);
  }, []);
  
  return {
    // State
    isDragging,
    draggedOrder,
    dragPosition,
    targetZone,
    
    // Handlers
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    cancelDrag,
  };
};

export default useTouchDragDeploy;

